import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import ChatAttachIcon from "../../../../assets/images/appointment-attach-icon.svg";
import CrossIcon from "../../../../assets/images/CrossIcon.svg";
import downloadIcon from "../../../../assets/images/download.svg";
import whiteDownloadIcon from "../../../../assets/images/white-download.svg";
import whiteImageIcon from "../../../../assets/images/white-image-icon.svg";
import blackImageIcon from "../../../../assets/images/black-image-icon.svg";
import whitePdfIcon from "../../../../assets/images/white-pdf-icon.svg";
import blackPdfIcon from "../../../../assets/images/black-pdf-icon.svg";
import whiteDocxIcon from "../../../../assets/images/white-docx-icon.svg";
import blackDocxIcon from "../../../../assets/images/black-docx-icon.svg";
import NoImage from "../../../../assets/images/no-image-avatar.png";
import { IMAGE_URL } from "../../../../app_url";
import {
  chatMessgaeDateFormat,
  getFileExtension,
} from "../../../../utils/commonData";
import {
  fetchPatientMessageDetailsApi,
  sendAppointmentMessageApi,
} from "../../../../services/apiService";
import "./chat-with-patient-card.css";

const PatientChatSkeleton = () => (
  <li
    className="patient-chat-skeleton"
    data-testid="patient-chat-skeleton"
    aria-label="Loading patient messages"
  >
    {[0, 1, 2].map((item) => (
      <div className="patient-chat-skeleton__message" key={item}>
        <Skeleton circle width={38} height={38} />
        <div className="patient-chat-skeleton__content">
          <Skeleton width={120} height={12} />
          <Skeleton width={item === 1 ? "65%" : "85%"} height={36} />
        </div>
      </div>
    ))}
  </li>
);

export default function ChatWithPatientCard({
    activeIndexes,
    toggleAccordion,
    patientId,
    DOC_doctorId,
    userData,
    onMessageSent,
}) {
    const chatListRef = useRef(null);
    const chatFileRef = useRef(null);
    const [patientDocMsgList, setPatientDocMsgList] = useState([]);
    const [loadedPatientId, setLoadedPatientId] = useState(null);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [messageFile, setMessageFile] = useState(null);
    const [messageSending, setMessageSending] = useState(false);

    useEffect(() => {
      let isCurrent = true;
      const isExpanded = activeIndexes.includes(0);

      const loadPatientMessages = async () => {
        if (!patientId) {
          setPatientDocMsgList([]);
          setLoadedPatientId(null);
          return;
        }
        if (!isExpanded || loadedPatientId === patientId) return;

        try {
          setPatientDocMsgList([]);
          setMessagesLoading(true);
          const formData = new FormData();
          formData.append("from_id", patientId);
          const response = await fetchPatientMessageDetailsApi(formData);

          if (!isCurrent) return;
          if (response?.data?.res === true) {
            setPatientDocMsgList(response.data.data || []);
            setLoadedPatientId(patientId);
          } else {
            toast.error(
              response?.data?.msg ||
                "Something went wrong in server. Please try again.",
              { autoClose: 1500 },
            );
          }
        } catch (error) {
          if (!isCurrent) return;
          console.error(error);
          toast.error("Something went wrong in server. Please try again.", {
            autoClose: 1500,
          });
        } finally {
          if (isCurrent) setMessagesLoading(false);
        }
      };

      loadPatientMessages();
      return () => {
        isCurrent = false;
      };
    }, [activeIndexes, loadedPatientId, patientId]);

    useEffect(() => {
      if (chatListRef.current) {
        chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
      }
    }, [patientDocMsgList]);

    const handleChatFileChange = (event) => {
      const acceptedFileTypes = [
        "image/jpeg",
        "image/png",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const file = event.target.files[0];

      if (!file) {
        toast.warning("No file selected.", { autoClose: 1500 });
        return;
      }
      if (!acceptedFileTypes.includes(file.type)) {
        toast.warning(
          "Invalid file type. Please select a JPG, PNG, PDF, or DOCX file.",
          { autoClose: 1500 },
        );
        event.target.value = "";
        return;
      }

      setMessageFile(file);
    };

    const handleDownload = async (fileUrl, fileName) => {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error("Network response was not ok");

        const blobUrl = window.URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success("Downloaded successfully", { autoClose: 1500 });
      } catch (error) {
        console.error("Error downloading the file:", error);
        toast.error("Failed to download the file.", { autoClose: 1500 });
      }
    };

    const handleSendMessage = async () => {
      if (!messageText && !messageFile) {
        toast.warning("Please enter a message or select a file to send.", {
          autoClose: 1500,
        });
        return;
      }
      if (!patientId) return;

      try {
        setMessageSending(true);
        const formData = new FormData();
        formData.append("appointment_id", "0");
        formData.append("message", messageText || "");
        formData.append("message_file", messageFile || "");
        formData.append("linked_to", patientId);
        formData.append("to_id", patientId);
        formData.append("types", "patient");

        const response = await sendAppointmentMessageApi(formData);
        if (response?.data?.res === true) {
          const sentMessage = response.data.data;
          sentMessage.to = `${sentMessage.to_first_name} ${sentMessage.to_last_name}`;
          sentMessage.from_profile_pic = userData?.users?.profile_pic;
          setPatientDocMsgList((messages) => [...messages, sentMessage]);
          setMessageFile(null);
          setMessageText("");
          onMessageSent?.();
        } else {
          toast.error(response?.data?.msg, { autoClose: 1500 });
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", {
          autoClose: 1500,
        });
      } finally {
        setMessageSending(false);
      }
    };

    return (
        <div className="accordion-card">
                          <div
                            className={`accordion-header accordion-header1 ${activeIndexes.includes(0) ? "active" : ""}`}
                            onClick={() => toggleAccordion(0)}
                          >
                            Chat with Patient
                          </div>
                          {activeIndexes.includes(0) && (
                            <div className="accordion-content accordion-content2">
                              <div className="PatientsChatCard PatientsChatCardPfl">
                                <div className="BottomChatBox">
                                  <ul className="ChatList" ref={chatListRef}>
                                    {messagesLoading ? (
                                      <PatientChatSkeleton />
                                    ) : (() => {
                                      const filteredPatientMsgs = (
                                        patientDocMsgList || []
                                      ).filter(
                                        (item) =>
                                          parseInt(item?.from_id) ===
                                          parseInt(DOC_doctorId) ||
                                          parseInt(item?.to_id) ===
                                          parseInt(DOC_doctorId),
                                      );
                                      return filteredPatientMsgs.length > 0 ? (
                                        filteredPatientMsgs.map((item, index) => (
                                          <li
                                            key={item?.id || `${item?.date}-${index}`}
                                            className={
                                              item?.from_id === userData?.users?.id
                                                ? "UserList"
                                                : ""
                                            }
                                          >
                                            <div className="ChatInfoTop">
                                              <div className="ChatUserImg">
                                                <img
                                                  src={
                                                    item?.from_profile_pic_url
                                                      ? item?.from_profile_pic_url
                                                      : NoImage
                                                  }
                                                  className="profileImg"
                                                  alt={item?.from || "Chat participant"}
                                                />
                                              </div>
                                              <div className="ChatUserInfo">
                                                <h4>
                                                  {item?.to_id ===
                                                    userData?.users?.id ? (
                                                    <>
                                                      {" "}
                                                      {item?.types === "refer" ? (
                                                        <>
                                                          {item?.from} |{" "}
                                                          <span className="DoctorNotExist">
                                                            {" "}
                                                            Referrel patient{" "}
                                                            {item.link_patient_name}
                                                          </span>
                                                        </>
                                                      ) : item?.types === "doctor" &&
                                                        item?.link_patient_name ? (
                                                        <>
                                                          {item?.from} |{" "}
                                                          <span className="aboutPatientSpan">
                                                            About patient{" "}
                                                            {item.link_patient_name}
                                                          </span>
                                                        </>
                                                      ) : (
                                                        item?.from
                                                      )}
                                                    </>
                                                  ) : item?.types === "refer" ? (
                                                    <>
                                                      <span className="DoctorNotExist">
                                                        Referrel patient{" "}
                                                        {item.link_patient_name}
                                                      </span>
                                                      {` | You`}
                                                    </>
                                                  ) : item?.types === "doctor" &&
                                                    item?.link_patient_name ? (
                                                    <>
                                                      <span className="aboutPatientSpan">
                                                        About patient{" "}
                                                        {item.link_patient_name}
                                                      </span>
                                                      {` | You`}
                                                    </>
                                                  ) : (
                                                    "You"
                                                  )}
                                                </h4>
                                                <h5>
                                                  {chatMessgaeDateFormat(item?.date)}
                                                </h5>
                                                {/* <h5>Today at 17:30</h5> */}
                                              </div>
                                            </div>
                                            {item?.message ? (
                                              <div
                                                className={`DoctorChatContent ${item?.from_id !== userData?.users?.id ? "" : "DoctorChatContent2"}`}
                                              >
                                                {item?.message}
                                              </div>
                                            ) : null}
        
                                            {item?.file_name && item?.files ? (
                                              <div
                                                className={`DoctorChatContent ${item?.from_id !== userData?.users?.id ? "" : "DoctorChatContent2"} fileSection`}
                                              >
                                                {" "}
                                                {getFileExtension(item.file_name) ===
                                                  "pdf" ? (
                                                  item?.from_id ===
                                                    userData?.users?.id ? (
                                                    <img
                                                      src={whitePdfIcon}
                                                      className="downloadIcon normalIcon"
                                                      alt="downloadIcon"
                                                    />
                                                  ) : (
                                                    <img
                                                      src={blackPdfIcon}
                                                      className="downloadIcon normalIcon"
                                                      alt="downloadIcon"
                                                    />
                                                  )
                                                ) : getFileExtension(item.file_name) ===
                                                  "docx" ? (
                                                  item?.from_id ===
                                                    userData?.users?.id ? (
                                                    <img
                                                      src={whiteDocxIcon}
                                                      className="downloadIcon normalIcon"
                                                      alt="downloadIcon"
                                                    />
                                                  ) : (
                                                    <img
                                                      src={blackDocxIcon}
                                                      className="downloadIcon normalIcon"
                                                      alt="downloadIcon"
                                                    />
                                                  )
                                                ) : item?.from_id ===
                                                  userData?.users?.id ? (
                                                  <img
                                                    src={whiteImageIcon}
                                                    className="downloadIcon normalIcon"
                                                    alt="whiteImageIcon"
                                                  />
                                                ) : (
                                                  <img
                                                    src={blackImageIcon}
                                                    className="downloadIcon normalIcon"
                                                    alt="whiteImageIcon"
                                                  />
                                                )}
                                                {item?.file_name}{" "}
                                                {item?.from_id ===
                                                  userData?.users?.id ? (
                                                  <img
                                                    src={whiteDownloadIcon}
                                                    className="downloadIcon"
                                                    alt="downloadIcon"
                                                    onClick={() =>
                                                      handleDownload(
                                                        IMAGE_URL + item?.files,
                                                        item?.file_name,
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  <img
                                                    src={downloadIcon}
                                                    className="downloadIcon"
                                                    alt="downloadIcon"
                                                    onClick={() =>
                                                      handleDownload(
                                                        IMAGE_URL + item?.files,
                                                        item?.file_name,
                                                      )
                                                    }
                                                  />
                                                )}
                                              </div>
                                            ) : null}
                                          </li>
                                        ))
                                      ) : (
                                        <>
                                          <li className="EmptyChatSection">
                                            <div className="DoctorChatContent">
                                              Conversation is empty.
                                            </div>
                                          </li>
                                        </>
                                      );
                                    })()}
                                  </ul>
                                  <div className="BottomChatTextBox">
                                    <textarea
                                      className="FormTextarea"
                                      placeholder="Write a message here..."
                                      value={messageText}
                                      disabled={messagesLoading || !patientId}
                                      onChange={(e) => setMessageText(e.target.value)}
                                    ></textarea>
                                    <a className="ChatAttachIcon">
                                      {!messageFile ? (
                                        <>
                                          <img
                                            src={ChatAttachIcon}
                                            alt="Doctor image"
                                            onClick={() =>
                                              chatFileRef?.current?.click()
                                            }
                                          />
                                          <input
                                            type="file"
                                            accept="image/jpeg, image/png, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            hidden
                                            ref={chatFileRef}
                                            onChange={(e) => handleChatFileChange(e)}
                                          />
                                        </>
                                      ) : (
                                        <div className="selectedFileSection">
                                          <span>{messageFile?.name}</span>
                                          <span>
                                            <img
                                              src={CrossIcon}
                                              className="CrossIcon"
                                              onClick={() => setMessageFile(null)}
                                            />
                                          </span>
                                        </div>
                                      )}
                                    </a>
                                    <button
                                      className="ChatButton"
                                      disabled={
                                        messageSending || messagesLoading || !patientId
                                      }
                                      onClick={handleSendMessage}
                                    ></button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
    )
}
